/**
 * PrivacyEn - the privacy notice, in English
 * -------------------------------------------
 * THIS AND `PrivacyIt.jsx` ARE THE CANONICAL VERSIONS. They are what people
 * actually see, so they are what counts. `PrivacyPolicy.jsx` is the page
 * furniture and picks between them.
 *
 * THE TWO FILES MUST SAY THE SAME THING. Change one and change the other in
 * the same commit, and bump TERMS_VERSION in backend/config/features.js.
 * `npm run check-legal` compares their shape.
 *
 * A privacy notice does not remove liability. It satisfies the duty under
 * GDPR art. 13 to tell people what you do with their information. What
 * protects you is ACTUALLY DOING WHAT THIS SAYS.
 *
 * THAT LAST POINT COST US A REWRITE. An earlier version promised that
 * security logs were deleted after 30 days, that dormant accounts went after
 * two years, and that consent records went a year after closure. None of it
 * was true - no scheduled job, no TTL index, nothing deletes anything. Stating
 * a retention period you do not apply is its own breach, on top of the one it
 * describes.
 *
 * So: if you add a retention promise here, build the deletion first. If you
 * build the deletion, come back and say so.
 *
 * Written to be understandable by a 16-year-old, which the Garante expects
 * when a service is aimed at minors.
 */

import { Link } from 'react-router-dom';

const APP_NAME = import.meta.env.VITE_APP_NAME || 'ChoreConnect';
const CONTACT_EMAIL = 'hello@choreconnect.org';
const SAFETY_EMAIL = 'safety@choreconnect.org';

export default function PrivacyEn() {
  return (
    <>
      <div className="card">
        <h2 style={{ fontSize: 'var(--text-h3)' }}>The short version</h2>
        <p style={{ marginTop: 'var(--space-xs)' }}>
          We keep the details you give us so that people who need a chore done can find a student to
          do it. <strong>We never sell your information and we never use it for advertising.</strong>{' '}
          You can ask us at any time to show you what we hold, correct it, or delete it. If you are
          under 18, a parent or guardian has to agree before you can work.
        </p>
        <p style={{ marginTop: 'var(--space-xs)' }}>
          Two things worth knowing straight away:{' '}
          <strong>messages on this site are kept and can be read by a moderator</strong> if a
          conversation is reported (section 6), and{' '}
          <strong>if you publish when you are free, adults on the site can see it</strong> (section
          5).
        </p>
        <p className="text-sm text-muted" style={{ marginTop: 'var(--space-xs)' }}>
          The rest of this page sets out the detail the law requires us to give you.
        </p>
      </div>

      <hr className="divider" />

      {/* ============================================================= */}
      <h2>1. Who is responsible</h2>
      <p>
        The &ldquo;data controller&rdquo; - the person legally responsible for your information -
        is <strong>Evelyn Catelijne Brouwer</strong>.
      </p>
      <ul style={{ marginLeft: 'var(--space-md)' }}>
        <li>
          <strong>Email:</strong> <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
        </li>
        <li>
          <strong>Safety and reports:</strong>{' '}
          <a href={`mailto:${SAFETY_EMAIL}`}>{SAFETY_EMAIL}</a>
        </li>
        <li>
          {/* Filled in 28 August 2026, at the owner's instruction. GDPR art.
              13(1)(a) makes a postal address mandatory, and this was the last
              placeholder standing in either document.

              WRITTEN IN ITALIAN POSTAL ORDER on purpose - street, then postcode
              before the town, then the province in brackets. That is how an
              address is written here and how it has to be written to arrive.
              The Italian version of this page says exactly the same thing;
              if one is ever corrected, correct both. */}
          <strong>Postal address:</strong>{' '}
          Via Guglielmo Marconi 249, 21027 Ispra (VA), Italy
        </li>
      </ul>
      <p>
        We have not appointed a Data Protection Officer. We do not believe we are required to, and
        if that changes we will say so here and give their details.
      </p>

      {/* ============================================================= */}
      <h2>2. What we collect</h2>

      <h3>Everybody</h3>
      <ul style={{ marginLeft: 'var(--space-md)' }}>
        <li>
          <strong>Name and email address</strong> - to create your account and sign you in.
        </li>
        <li>
          <strong>Password</strong> - stored scrambled, so nobody at {APP_NAME} can read it.
        </li>
        <li>
          <strong>Your town and street, and if you choose it your precise location</strong> - to
          show how far away a job is, along roads and on foot. Precise location is optional and you
          can refuse it; pressing &ldquo;Use my precise location&rdquo; fills in your street and town
          for you, and you can correct either by hand. Your street is kept on your account and is
          shown to nobody else - other people are told the town and a distance. Distances are
          measured from the street on your account and from the street on the chore, and only when
          we can find both on the map; where we cannot, no distance is shown to anybody. Your
          browser&rsquo;s position is used only to suggest that street to you, never to measure
          anything. See section 7 for where street names come from.
        </li>
        <li>
          <strong>Phone number</strong> - optional.
        </li>
        <li>
          <strong>Profile photograph</strong> - optional, for both students and posters.
        </li>
        <li>
          <strong>Whether you have confirmed your email address</strong>, and a one-time code we
          send you to do it.
        </li>
      </ul>

      <h3>Students</h3>
      <ul style={{ marginLeft: 'var(--space-md)' }}>
        <li>
          <strong>The school you go to</strong>, as you type it - shown on your profile. We do not
          check it; the invite code is what decides who can join.
        </li>
        <li>
          <strong>Date of birth and school year</strong> - to check you are old enough to work,
          to keep your age up to date as you get older, and to show posters who they are hiring.
          Other people see your age, never your date of birth.
        </li>
        <li>
          <strong>What you write about yourself</strong> - optional, and public.
        </li>
        <li>
          <strong>Bank details</strong> - optional. Only ever visible to you; never shown to any
          other user and never sent anywhere.
        </li>
        <li>
          <strong>Your parent or guardian&apos;s name and email</strong> - if you are under 18,
          kept as the record that they were asked and what they answered.
        </li>
        <li>
          <strong>Your availability</strong>, if you publish it - what you can help with, which
          town, and which times of the week you are usually free.
        </li>
      </ul>

      <h3>Everything you do here</h3>
      <ul style={{ marginLeft: 'var(--space-md)' }}>
        <li>
          <strong>Chores, applications, offers, reviews and messages</strong> you create. A chore
          always includes the street address where the work is.
        </li>
        <li>
          <strong>A donation, if you choose to make one</strong> - the amount, the date, and
          Stripe&apos;s reference for the payment, linked to your account. Your card details go to
          Stripe and never reach us.
        </li>
        <li>
          <strong>Reports</strong> you make about somebody, and who you have blocked.
        </li>
        <li>
          <strong>Notification settings</strong>, and - if you turn on alerts - a{' '}
          <strong>subscription for your browser or phone</strong> so we can send them. That
          identifies the device, not you personally.
        </li>
      </ul>

      <h3>Records we keep about how the site is run</h3>
      <ul style={{ marginLeft: 'var(--space-md)' }}>
        <li>
          <strong>Your IP address and the time</strong> when you sign up, make a report, or when a
          moderator acts. We use these to keep the site secure and to look into abuse.
        </li>
        <li>
          <strong>An activity log</strong> recording actions such as accounts being created,
          suspended or reinstated, consent being given or withdrawn, chores being posted or taken
          down, and <strong>any occasion a moderator opens a conversation</strong>. See section 6.
        </li>
        <li>
          <strong>Moderation records</strong> - if an account is suspended, we keep the reason, when
          it started, when it ends, and who decided. If a chore or offer is taken down, we keep the
          reason.
        </li>
        <li>
          <strong>Anything you write in &ldquo;Report an issue&rdquo;</strong>, and our replies.
          See section 6.
        </li>
        <li>
          <strong>For moderators only</strong> - the extra security data for their two-step login.
        </li>
      </ul>

      {/* ============================================================= */}
      <h2>3. Why we are allowed to hold it</h2>
      <p>
        The law requires us to give a legal basis for each purpose. Ours are:
      </p>
      <ul style={{ marginLeft: 'var(--space-md)' }}>
        <li>
          <strong>To provide the service you asked for</strong> - your account, chores,
          applications, offers, reviews and messages.{' '}
          <em>Basis: performance of a contract (art. 6(1)(b)).</em> We could not run {APP_NAME}{' '}
          without these.
        </li>
        <li>
          <strong>To keep people safe and stop abuse</strong> - reports, blocks, moderation records,
          the activity log, IP addresses, and a moderator reading a reported conversation.{' '}
          <em>Basis: our legitimate interests (art. 6(1)(f)).</em> The interest is protecting users,
          most of whom are children, from harm on a service that introduces them to adults. We have
          weighed that against your privacy and keep only what that purpose needs.
        </li>
        <li>
          <strong>To keep the record that a parent agreed</strong> - the consent record itself.{' '}
          <em>Basis: legal obligation and legitimate interests (art. 6(1)(c) and (f)).</em> It
          exists to protect the child concerned and to show we asked.
        </li>
        <li>
          <strong>To record a donation you chose to make</strong> - so the money can be matched
          against Stripe&apos;s records and accounted for.{' '}
          <em>Basis: performance of a contract (art. 6(1)(b)), and the legal obligation to keep
          records of money received (art. 6(1)(c)).</em>
        </li>
        <li>
          <strong>To keep your account secure</strong> - confirming your email address, password
          resets, two-step login for moderators.{' '}
          <em>Basis: performance of a contract, and legitimate interests in security.</em>
        </li>
        <li>
          <strong>Optional things you switch on</strong> - your precise location, your photograph,
          what you write about yourself, and phone alerts.{' '}
          <em>Basis: your consent (art. 6(1)(a)).</em> You can withdraw it at any time by removing
          them or turning them off, and that does not affect anything done beforehand.
        </li>
      </ul>
      <p>
        <strong>We do not send marketing and we have no advertising.</strong> Everything we email
        you is either something you asked for or something you need to receive.
      </p>

      {/* ============================================================= */}
      <h2>4. Who can see what</h2>
      <p>This is the most important section on the page, so it is short and specific.</p>
      <ul style={{ marginLeft: 'var(--space-md)' }}>
        <li>
          <strong>Anybody, including people not signed in</strong> - chores on the marketplace, and
          the poster&apos;s first name. The marketplace carries no student information at all.
        </li>
        <li>
          <strong>Signed-in users</strong> - your first name and last initial, your town, your star
          rating and your reviews, and your photograph if you added one. For students, also your
          age, your school, your school year, what you wrote about yourself, and the categories you
          chose.
        </li>
        <li>
          <strong>Adult posters, if you are a student who publishes availability</strong> - what you
          can help with, your town, and the times you are usually free. See section 5.
        </li>
        <li>
          <strong>Only the people involved in a chore</strong> - your full name, and the exact
          address. The poster sees the student they accepted and the backup; those students see the
          address. Nobody else does.
        </li>
        <li>
          <strong>Only you</strong> - your password, your bank details, your phone number, your
          precise coordinates, your date of birth, your parent or guardian&apos;s contact details,
          and any donation you have made.
        </li>
        <li>
          <strong>Moderators</strong> - see section 6.
        </li>
      </ul>

      {/* ============================================================= */}
      <h2>5. If you publish when you are free</h2>
      <p>
        Students can publish an <strong>offer</strong>: what they can help with, which town, and a
        weekly timetable of when they are usually free. This is optional and you can pause or delete
        it at any time.
      </p>
      <p>
        <strong>Please understand what it means.</strong> Everywhere else on {APP_NAME} you go
        looking for work. This works the other way round: adults on the site browse students and
        ask. So:
      </p>
      <ul style={{ marginLeft: 'var(--space-md)' }}>
        <li>
          <strong>Nobody signed out can see any of it</strong>, and neither can other students. Only
          signed-in adult posters.
        </li>
        <li>
          <strong>If you are under 18, your parent has already agreed to this specifically</strong>,
          not to chores in general.
        </li>
        <li>
          <strong>Nobody can book you.</strong> A poster can only ask. You see the job, the pay and
          the town, and you say yes or no. You never have to give a reason for saying no.
        </li>
        <li>
          <strong>No address is shared until you say yes.</strong>
        </li>
      </ul>
      <p>
        Think about what you write. &ldquo;Free after school on Tuesdays&rdquo; is fine. Anything
        that says which bus you take, which gate you leave by, or where you will be standing is not.
      </p>

      {/* ============================================================= */}
      <h2>6. Messages, and when a moderator can read them</h2>
      <p>
        A student can ask the poster a question about a chore that is still open for applications,
        and once they accept a chore the two of them can message each other about it. Only the
        student can start either of those - a poster can never message a student who has not written
        to them first - and a question becomes read-only as soon as the job is booked.
      </p>
      <p>
        <strong>There is one conversation an adult can start, and it is deliberately narrow.</strong>{' '}
        An adult can put a single question to a student about the availability they have published.
        The student is notified, and nothing further can be sent unless they answer - answering is
        what opens the conversation, and ignoring it closes the matter. An adult may have five
        unanswered questions out at a time. We need to be clear about how private all of this is.
      </p>
      <ul style={{ marginLeft: 'var(--space-md)' }}>
        <li>
          <strong>Messages are stored and are not encrypted end-to-end.</strong> That is the
          opposite of what you would build for two adults, and it is deliberate: a moderator has to
          be able to read a conversation somebody has complained about.
        </li>
        <li>
          <strong>Messages are never deleted</strong>, even when an account is closed. A moderator
          can hide a message from the two people in the conversation; the text stays in our records
          because it may be evidence.
        </li>
        <li>
          <strong>A moderator can open a conversation in exactly two situations:</strong> somebody
          reported it - the person, the chore, or a message in it - or our system noticed contact
          details being shared, which is the pattern of a conversation being moved somewhere nobody
          can see it. <strong>Every other conversation is closed to them</strong>, and the server
          refuses it rather than merely hiding the link.
        </li>
        <li>
          <strong>Every time a moderator opens a conversation it is written to the activity
          log</strong> - who read it, whose conversation, when, and how many messages were shown.
        </li>
        <li>
          <strong>A thread closes seven days after the job.</strong> It is not an open-ended channel.
        </li>
      </ul>
      <p>
        Nobody reads conversations routinely, and nothing is scanned by a person unless one of the
        two situations above applies.
      </p>

      <h3>If you report an issue</h3>
      <p>
        Separately from the above, every member has a &ldquo;Report an issue&rdquo; conversation for
        telling us the site itself is broken. It works differently from the conversations described
        above, and the differences matter.
      </p>
      <ul style={{ marginLeft: 'var(--space-md)' }}>
        <li>
          <strong>A moderator reads all of it, always.</strong> The two-situations rule above does
          not apply, because you are writing to them rather than to another member. Reading it is
          not written to the activity log for the same reason; replying is.
        </li>
        <li>
          <strong>You are not told which moderator answered.</strong> Replies appear as
          &ldquo;Moderator&rdquo;, with no name, photograph or profile, and the site never sends
          your browser that information. A moderator is also an ordinary member here, and somebody
          who has raised a problem should not spend the following week recognising them.
        </li>
        <li>
          <strong>It is kept, including after you close your account</strong>, and is never deleted.
          People do not sort their problems into our categories before typing, so a conversation
          about something being broken is sometimes the only written record that somebody was
          uncomfortable about a person.
        </li>
      </ul>

      {/* ============================================================= */}
      <h2>7. Who else processes your data</h2>
      <p>
        We do not sell your information and we do not use it for advertising. We share it only with
        the services that make the site work:
      </p>
      <ul style={{ marginLeft: 'var(--space-md)' }}>
        <li>
          <strong>MongoDB Atlas</strong> (EU region) - stores the database.
        </li>
        <li>
          <strong>Hetzner</strong> (Germany) - runs the server that delivers the website.
        </li>
        <li>
          <strong>Cloudflare</strong> - sits in front of the site, encrypts the connection and
          filters attacks, so every request passes through it.
        </li>
        <li>
          <strong>Brevo</strong> (France) - sends our email, such as password resets, parental
          consent requests and address confirmations.
        </li>
        <li>
          <strong>Cloudinary</strong> - stores profile photographs.
        </li>
        <li>
          <strong>Your browser or phone maker&apos;s push service</strong> (Google, Apple, Mozilla
          or Microsoft, depending on your device) - delivers alerts, if you turn them on. They see
          that a message was sent to your device.
        </li>
        <li>
          <strong>Stripe</strong> - only if you choose to donate, and only then. Stripe receives
          your email address and the amount, collects your card details itself, and emails you the
          receipt. We never see your card details. There is no charge for using {APP_NAME}.
        </li>
      </ul>
      <p>
        <strong>Street names, routes, and your position.</strong> When you press &ldquo;Use my
        precise location&rdquo;, the street is looked up on our own server, in a copy of the map data
        from OpenStreetMap (&copy; OpenStreetMap contributors). Distances along roads are worked out
        on our own server too, by a route planner using the same map data. Your position, the street
        you type and the places we measure between are not sent to OpenStreetMap or to any other map
        or routing service. Since 18 September 2026 your browser&rsquo;s position is not sent to us
        while you browse at all: the chore list is measured from the street on your account.
      </p>
      <p>
        We would also pass information to the police, a court or another authority where the law
        required it, or where somebody is at risk of serious harm.
      </p>
      <p>
        <strong>Where your data is.</strong> Your account and everything in it are stored in the
        European Union - the database in an EU region, the server in Germany.{' '}
        <strong>Cloudflare, Cloudinary and Stripe are, or belong to, United States
        companies</strong> and may process data
        there; where that happens it is covered by an appropriate safeguard under Chapter V of the
        GDPR, which for these providers is either their certification under the EU-US Data Privacy
        Framework or the European Commission&apos;s standard contractual clauses. We have a data
        processing agreement with each provider.
      </p>

      {/* ============================================================= */}
      <h2>8. Cookies and what we store in your browser</h2>
      <p>
        <strong>We do not use any analytics, tracking or advertising cookies. There is no
        third-party tracking on this site at all.</strong>
      </p>
      <ul style={{ marginLeft: 'var(--space-md)' }}>
        <li>
          <strong>Your login token</strong>, kept in local storage so you stay signed in. It lasts
          up to a year, so that a site you have added to your home screen does not log you out
          between visits, and it disappears the moment you log out. It is strictly necessary for
          the site to work and is not shared with anybody.
        </li>
        <li>
          <strong>A signup form you have not finished</strong>, kept only while that browser tab is
          open, so that reading this notice or the terms part-way through signing up does not lose
          what you had typed. It is thrown away when the account is created, when the tab is
          closed, or after two hours - whichever comes first. It never leaves your device.
        </li>
        <li>
          <strong>Cloudflare may set a security cookie</strong> to tell real visitors from automated
          attacks. It is strictly necessary for security and is not used to track you across other
          websites.
        </li>
      </ul>
      <p>
        Because all three are strictly necessary, no consent banner is required for them. If we ever add
        anything that is not strictly necessary, we will ask you first.
      </p>

      {/* ============================================================= */}
      <h2>9. How long we keep it</h2>
      <p>
        <strong>We would rather tell you what actually happens than quote a tidy number.</strong>
      </p>
      <ul style={{ marginLeft: 'var(--space-md)' }}>
        <li>
          <strong>Your account</strong> - until you close it. If you stop using {APP_NAME} you can
          close your account at any time from your settings, and we would encourage it.
        </li>
        <li>
          <strong>When you close your account</strong> - your name, email, phone number, photograph,
          location, date of birth, bank details and anything you wrote about yourself are removed
          immediately. Your
          photograph is deleted from Cloudinary at the same time.
        </li>
        <li>
          <strong>Chores, reviews, messages and anything you reported as an issue</strong> - these
          remain after an account is closed, with your name replaced by &ldquo;Deleted user&rdquo;.
          We keep them because deleting them would take other people&apos;s history and reviews with
          them, and because messages may be needed to look into something that happened.
        </li>
        <li>
          <strong>Parental consent records</strong> - kept while the account exists and after it
          closes, as the record that we asked and what the answer was.
        </li>
        <li>
          <strong>Donation records</strong> - kept after an account is closed, because records of
          money received have to be kept for accounting and tax purposes.
        </li>
        <li>
          <strong>The activity log, moderation records, reports and the IP addresses attached to
          them</strong> - kept as the record of what was done and why.
        </li>
      </ul>
      <p>
        <strong>From 25 September 2026 these are deleted on a timer.</strong> Before that date
        nothing here was ever deleted, and this notice said so. The periods are:
      </p>
      <ul>
        <li>
          <strong>An activity-log entry - two years</strong>, then it is deleted.
        </li>
        <li>
          <strong>An IP address - one year</strong>, whether it sits on a log entry or on a report.
          The record itself stays; the address is removed from it. It is the part that points at a
          house rather than at an account, so it is the part we hold for the shortest time.
        </li>
        <li>
          <strong>A report a moderator has closed - three years</strong> from the day it was
          closed, then it is deleted. <strong>A report nobody has dealt with is never deleted on a
          timer</strong> - it stays until a person closes it.
        </li>
      </ul>
      <p>
        You can also ask us to delete your personal data at any point (section 10) - we will do it
        unless we have a legal reason to keep something, and we will tell you if so.
      </p>

      {/* ============================================================= */}
      <h2>10. Your rights</h2>
      <p>Under the GDPR you can ask us to:</p>
      <ul style={{ marginLeft: 'var(--space-md)' }}>
        <li>show you everything we hold about you (access)</li>
        <li>correct anything wrong (rectification)</li>
        <li>delete your personal details (erasure)</li>
        <li>stop using your data while we look into a complaint (restriction)</li>
        <li>give you a copy in a format you can take elsewhere (portability)</li>
        <li>
          stop processing we do on the basis of our legitimate interests (objection) - tell us why
          it affects you and we will stop unless we have compelling grounds to continue
        </li>
        <li>withdraw any consent you gave for the optional parts, at any time</li>
      </ul>
      <p>
        Email <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>. We will reply within one
        month, and it costs nothing. We may ask you to confirm who you are first, so that somebody
        else cannot ask for your data.
      </p>
      <p>
        <strong>If you are under 18</strong>, your parent or guardian can do any of this for you.
        They can also withdraw their permission at any time, using the link in the email they were
        sent - it does not expire. Withdrawing permission stops you applying for work, publishing
        availability or being booked; it does not delete your account, and both of you are told.
      </p>

      {/* ============================================================= */}
      <h2>11. Children</h2>
      <p>
        You must be at least 16 to sign up as a student, because 16 is the minimum working age in
        Italy. We ask your date of birth and take your answer at face value - we do not verify it.
        Once you have signed up it cannot be changed from your account, so that nobody can use it
        to get round the age rules; if it is wrong, tell us.
      </p>
      <p>
        <strong>If you are under 18, we email your parent or guardian and wait.</strong> Until they
        confirm, you cannot apply for work, publish when you are free, or be booked by anybody. We
        deliberately do not accept a tickbox from you instead: a box you tick yourself does not show
        that a parent ever saw the request, let alone agreed to it. From your eighteenth birthday
        their confirmation is no longer needed.
      </p>
      <p>We tell them what they are agreeing to, in five parts:</p>
      <ul style={{ marginLeft: 'var(--space-md)' }}>
        <li>that you can apply for jobs and travel to somebody&apos;s address</li>
        <li>
          that a job can run over several days, so you can agree once to visiting the same
          adult&apos;s home many times over
        </li>
        <li>that you can publish when you are free, for adults on the site to see</li>
        <li>
          that you can ask about a job before applying, that you and the person whose job you take
          can message each other here, and that those messages are kept and can be read by a
          moderator if reported
        </li>
        <li>that you can choose to receive alerts on your phone</li>
      </ul>
      <p>
        If we ever change what happens to you here, we ask them again rather than rely on the answer
        they already gave.
      </p>
      <p>
        In Italy anyone aged 14 or over can consent to their own data being handled. We still ask a
        parent, because this is about more than data: it involves agreeing to our terms and
        travelling to somebody else&apos;s home.
      </p>

      {/* ============================================================= */}
      <h2>12. Keeping your information safe</h2>
      <p>
        Passwords are stored scrambled, so nobody at {APP_NAME} can read them. The connection is
        encrypted, access to the database is restricted, and accounts that can read reports and
        private conversations need a second step to log in beyond a password.
      </p>
      <p>
        <strong>No system is ever completely secure.</strong> If a breach happened and it was likely
        to put people at risk, we would tell the Garante within 72 hours of becoming aware of it, as
        the law requires, and we would tell you without undue delay where the risk to you was high.
      </p>

      {/* ============================================================= */}
      <h2>13. Automated decisions</h2>
      <p>
        <strong>We do not make automated decisions that have a legal or similarly significant effect
        on you.</strong> A person always decides who gets a chore.
      </p>
      <p>
        Some things on the site are automatic, and you should know what they are: the marketplace is
        sorted by distance, pay, date or how closely a chore matches your search, with chores that
        only need a backup listed after the rest; job alerts are matched to the categories, minimum pay and
        distance a student chose, and held back during their quiet hours; and a simple pattern check
        flags a message that looks like it is sharing contact details, which makes that conversation
        readable by a moderator (section 6). None of these decides anything about you on its own.
      </p>

      {/* ============================================================= */}
      <h2>14. If you are unhappy</h2>
      <p>
        Please tell us first - we would rather fix it. Email{' '}
        <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
      </p>
      <p>
        You also have the right to complain to the Italian supervisory authority, the{' '}
        <strong>Garante per la protezione dei dati personali</strong> (
        <a href="https://www.garanteprivacy.it" target="_blank" rel="noopener noreferrer">
          garanteprivacy.it
        </a>
        ), or to the authority where you live.
      </p>

      {/* ============================================================= */}
      <h2>15. Changes</h2>
      <p>
        If we change anything important we will update the date at the top and tell you. If a change
        materially affects students under 18, we ask their parent or guardian again rather than
        relying on the answer they already gave.
      </p>

      <hr className="divider" />

      <p className="text-sm text-muted">
        See also our <Link to="/terms">terms of service</Link>.
      </p>
    </>
  );
}
